'use client';
import { useSearchParams } from 'next/navigation';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { EventDetailsType } from '@/api/config/types';
import { SendMessage } from '@/app/[lang]/(web)/contact-us/components/ContactUsForm';
import { SourceData } from '@/app/[lang]/(web)/events/[slug]/components/EventDetailsForm';
import Image from '@/components/core/Image';
import Input from '@/components/core/Input';
import Modal from '@/components/core/Modal';
import Toaster from '@/components/popups/Toaster';
import { useTranslation } from '@/hooks/useTranslations';

import { validateForm } from './validationFunction';

interface MeetingBookProps {
  modalState?: boolean;
  meeting: EventDetailsType;
  onControll?: () => void;
}

interface IFormData {
  name_surname: string;
  email: string;
  phone: string;
  message: string;
  sourceData: SourceData | null;
}
const MeetingBook: FC<MeetingBookProps> = ({ meeting, onControll, modalState }) => {
  const { t } = useTranslation();
  const searchParams = useSearchParams();

  const [formData, setFormData] = useState<IFormData>({
    name_surname: '',
    email: '',
    phone: '',
    message: '',
    sourceData: null,
  });

  const [errors, setErrors] = useState({
    name_surname: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors((prev) => ({ ...prev, [event.target.name]: '' }));
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const onSubmit = async () => {
    const validation = validateForm(formData);
    if (Object.values(validation).filter(Boolean).length) {
      return setErrors(validation);
    }
    return await SendMessage({ ...formData, id: meeting.id }, 'event').then(() => {
      setFormData({ name_surname: '', email: '', phone: '', message: '', sourceData: null });
      toast(<Toaster type="success" description="Messagesent-Text" />, {
        duration: 3000,
      });
      window.dataLayer?.push?.({
        event: 'form_submission',
        form_class: `bm_${meeting.title.replaceAll(' ', '_').toLocaleUpperCase()}_${meeting.id}`,
        form_id: meeting.country.toUpperCase(),
      });
      onControll?.();
    });
  };

  useEffect(() => {
    // Convert URLSearchParams to a plain object
    if (searchParams) {
      const params = Object.fromEntries(searchParams.entries());
      setFormData({ ...formData, sourceData: params as SourceData });
    }
  }, [searchParams]);

  useEffect(() => {
    const sourceData = sessionStorage.getItem('sourceData');
    if (sourceData) {
      let data = JSON.parse(sourceData);
      setFormData({ ...formData, sourceData: data });
    }
  }, []);

  if (!meeting) {
    onControll?.();
  }

  return (
    <Modal
      onClose={onControll}
      showModal={modalState}
      title={t('Homepage-Form-Title-Bookameeting')}
      description={t('Homepage-Form-Title')}
    >
      <div className="popupContactBookMeeting">
        <div className="popupContactBookMeetingTop">
          <div className="popupMeetingInfoWrp">
            <div className="popupMeetingInfo">
              <p className="popupMeetingInfoCountry">{meeting.title}</p>
              <p
                dangerouslySetInnerHTML={{ __html: meeting.standNumber }}
                className="popupMeetingInfoNumber"
              />
            </div>
            <p className="popupMeetingInfoDate">{meeting.eventDate}</p>
          </div>
          <div className="popupMeetingCountryWrp">
            <div className="countyFlag">
              <Image width={36} height={24} alt="certificate-flag" src={meeting.countryLogo} />
            </div>
            <p className="popupMeetingCountryName">{meeting.country}</p>
          </div>
        </div>
        <div className="popupContactMeFormWrp">
          <Input
            name="name_surname"
            onChange={handleChange}
            placeholder={t('Contactus-Form-Name')}
            className="popOkInput darkBg"
            value={formData.name_surname}
          />
          {errors.name_surname && <p className="error-message">Please fill name correctly</p>}
          <Input
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('Contactus-Form-Email')}
            className="popOkInput darkBg"
          />
          {errors.email && <p className="error-message">Please fill email correctly</p>}
          <Input
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={t('Contactus-Form-Phonenumber')}
            className="popOkInput darkBg"
          />
          {errors.phone && <p className="error-message">Please fill phone correctly</p>}
          <textarea
            name="message"
            value={formData.message}
            placeholder={t('Contactus-Form-Message')}
            className="popOkTextarea type2"
            onChange={(event) => handleChange(event as any)}
          />
          {errors.message && <p className="error-message">Please fill message</p>}
        </div>
        <div className="contactUsBtnBlock">
          <div className="contactUsBtnBlockInner">
            <button
              onClick={onSubmit}
              className="button btnTypeBig btnTypeOrange fullWidth"
              id={`${(meeting.title === 'SBC Summit' ? 'sbc-portugal' : meeting.title).toLowerCase().replaceAll(' ', '-')}-send`}
            >
              <p>{t('Contactus-Form-Button-Send')}</p>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MeetingBook;
